import React, { useEffect, useState } from 'react';
import ProductLayout from '../components/ProductLayout';
import { Puzzle, ShieldCheck, Printer, Lock, Cloud, Smartphone, Star } from 'lucide-react';
import heroVideo from '../assets/LumoChart-hero.mp4';

// 🧩 Logos
import lumochartLogoLight from '../assets/lumochart-login.png';
import lumochartLogoDark from '../assets/lumochart-login-dark.png';

export default function LumoChartLanding() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // ✅ Sync with global dark mode
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      const dark = root.classList.contains('dark');
      setIsDark(dark);
      localStorage.setItem('darkMode', JSON.stringify(dark));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    setIsDark(root.classList.contains('dark'));
    return () => observer.disconnect();
  }, []);

  const lumochartLogo = isDark ? lumochartLogoDark : lumochartLogoLight;

  return (
    <ProductLayout accent="chart">

      {/* Hero Section */}
      <section className="py-28 md:py-40 text-center">
        <div className="container mx-auto px-6 mt-20">
          <img
            src={lumochartLogo}
            alt="LumoChart Logo"
            className="h-24 md:h-32 mx-auto mb-8 object-contain drop-shadow-lg"
          />

          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight text-gray-900 dark:text-white">
            Clinical documentation,<br className="hidden md:block" /> reimagined.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Modular. Reliable. Designed for every setting—from ED to outpatient. 
            LumoChart adapts to your workflow, not the other way around.
          </p>

          <div className="mt-10 flex justify-center space-x-4">
            <a
              href="#features"
              className="bg-blue-600 text-white font-semibold px-8 py-3 rounded-full hover:bg-blue-700 transition shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Explore Features
            </a>
            <a
              href="/login"
              className="bg-gray-800 dark:bg-zinc-700 text-white font-semibold px-8 py-3 rounded-full hover:bg-gray-900 dark:hover:bg-zinc-600 transition shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Create Account
            </a>
          </div>

          {/* Demo Video */}
          <div className="mt-20 max-w-6xl mx-auto">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl overflow-hidden ring-1 ring-black/5">
              <video
                src={heroVideo}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-zinc-900 transition-colors duration-300">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              A Foundation You Can Trust
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Built from the ground up for speed, security, and flexibility.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={Puzzle}
              title="Modular Architecture"
              text="Drop-in components for inpatient, consult, ED, and more."
            />
            <FeatureCard
              icon={ShieldCheck}
              title="Privacy-First Design"
              text="PHI minimization and HIPAA-focused infrastructure."
            />
            <FeatureCard
              icon={Printer}
              title="Clinical-Grade Exports"
              text="Professional, branded PDFs ready for any EMR."
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              Trusted by Leading Clinicians
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              LumoChart helps providers focus on what matters most—their patients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <TestimonialCard
              quote="LumoChart is the documentation tool I've been waiting for."
              name="Dr. Stephanie Reed"
              title="Internal Medicine, General Hospital"
            />
            <TestimonialCard
              quote="In the ED, speed and clarity are everything."
              name="Dr. Bryan Thorne"
              title="Emergency Physician, City Medical Center"
            />
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section id="compliance" className="py-20 bg-white dark:bg-zinc-900 transition-colors duration-300">
        <div className="container mx-auto px-6">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl p-10 md:p-16 flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                Built for Trust & Compliance
              </h2>
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                HIPAA-aligned infrastructure with encrypted storage and secure routing.
              </p>
            </div>
            <div className="flex space-x-8">
              <ComplianceBadge icon={Lock} label="HIPAA Compliant" />
              <ComplianceBadge icon={Cloud} label="Secure Cloud" />
              <ComplianceBadge icon={Smartphone} label="App Store Ready" />
            </div>
          </div>
        </div>
      </section>

    </ProductLayout>
  );
}

function FeatureCard({ icon: Icon, title, text }) {
  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl p-8 shadow-sm hover:shadow-xl border border-gray-100 dark:border-zinc-700 transition transform hover:-translate-y-2">
      <div className="bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 rounded-lg h-12 w-12 flex items-center justify-center mb-4">
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{text}</p>
    </div>
  );
}

function ComplianceBadge({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-gray-100 dark:bg-zinc-800 rounded-full p-4 mb-2">
        <Icon size={28} className="text-blue-600 dark:text-blue-400" />
      </div>
      <span className="text-gray-600 dark:text-gray-300 text-sm font-medium">{label}</span>
    </div>
  );
}

function TestimonialCard({ quote, name, title }) {
  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl p-8 shadow-sm hover:shadow-xl border border-gray-100 dark:border-zinc-700 transition transform hover:-translate-y-2">
      <Star size={32} className="text-yellow-400 mb-3" />
      <p className="italic text-gray-700 dark:text-gray-300 mb-4">“{quote}”</p>
      <p className="font-semibold text-gray-900 dark:text-white">{name}</p>
      <p className="text-gray-500 dark:text-gray-400 text-sm">{title}</p>
    </div>
  );
}
